import * as React from 'react'
import { ScrollView, Text, View, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import CGU from '@components/cgu/CGU'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'

import useNavigation from '@layout/useNavigation'

import { AuthScope } from '@api/enum'
import { VERSION_NAME } from '@app/config'

const SECTIONS = ['info', 'identification', 'notification', 'lang', 'signature', 'help'] as const

interface Props {
  onLogout: () => void
  onDeleteAccount: () => void
}

const MenuLayout = ({ onLogout, onDeleteAccount }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  // screen available only for connected user
  const user = useReducer(UserStore.store, (s) => s.user)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  if (!user) {
    return null
  }

  const ScopeRule = scopes.includes(AuthScope.UtilisateursInformationModification) ? [] : ['identification']
  const sections = React.useMemo(() => SECTIONS.filter((s) => !ScopeRule.includes(s)), [SECTIONS, ScopeRule])

  const renderItem = (item: (typeof SECTIONS)[number], index: number, list: readonly (typeof SECTIONS)[number][]) => (
    <View key={item}>
      <Item activeOpacity={0.9} onPress={() => navigation.push(`/account/${item}`)} key={item}>
        <Label>{i18n.t(`screens.account.mobileSections.${item}`)}</Label>
        <Icon color={theme.colors.primary} name="arrow" size={24} />
      </Item>
      {index < list.length - 1 && <Separator key={`${item}_separator`} />}
    </View>
  )

  return (
    <Root fixed>
      <Header>
        <Title>{i18n.t('screens.account.mobileTitle')}</Title>
      </Header>
      <ScrollView>
        <Name>{`${user.prenom} ${user.nom}`}</Name>
        {!!user.fonction && <Job>{user.fonction}</Job>}
        <ItemContainer>
          <Items>{sections.map(renderItem)}</Items>
        </ItemContainer>

        <Links>
          <CGU alignTextLeft />
          <VersionName>{i18n.t('common.version', { version: VERSION_NAME })}</VersionName>
        </Links>

        <DeleteContainer>
          <Button
            leftIcon="delete"
            label={i18n.t('screens.account.deleteAccount.title')}
            onClick={onDeleteAccount}
            status="active"
            color="cancel"
          />
        </DeleteContainer>
      </ScrollView>

      <LogoutContainer>
        <Button label={i18n.t('common.logout')} onClick={onLogout} status="active" />
      </LogoutContainer>
    </Root>
  )
}

export default MenuLayout

const Header = styled(View)`
  background-color: ${(props) => props.theme.colors.primary};
  padding: 16px;
  padding-top: 40px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.LargeTitle};
  color: ${(props) => props.theme.colors.background};
`

const Name = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
  padding-top: 16px;
  padding-left: 16px;
`

const Job = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  padding-left: 16px;
`
const ItemContainer = styled(View)`
  padding: 16px;
`

const Items = styled(View)`
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
`

const Item = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
`

const Label = styled(Text)`
  flex: 1;
  ${(props) => props.theme.fonts.Body};
`
const DeleteContainer = styled(View)`
  padding: 16px;
  margin-top: 24px;
`
const LogoutContainer = styled(View)`
  padding: 16px;
  z-index: 1;
  background-color: ${(props) => props.theme.colors.background};
`

const Separator = styled(View)`
  margin-left: 16px;
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
`

const Links = styled(View)`
  padding: 16px 16px 0;
`

const VersionName = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  color: ${(props) => props.theme.colors.secondaryText};
  margin: 16px 0 -16px;
`
